import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AsButtonDirective } from '@directives/as-button/as-button.directive';
import { ActivityStep } from '@models/activity.model';
import { UserAsset } from '@models/dashboard.model';
import {
  calculateItemsToDelete,
  createShoppingObject,
  createShoppingPriceObject
} from '@models/functions.model';
import { CartInfo } from '@models/session-details';
import { InCartObjects, ShoppingCartData, ShoppingCartItem } from '@models/shopping-cart.model';
import {
  ADMIN_OPTIONS,
  CART_ITEM_TYPE_ACCESSORY,
  CART_ITEM_TYPE_DEVICE,
  DeviceData,
  DeviceItemCarrier,
  DeviceItemData,
  DeviceItemDevice,
  DevicePrice,
  ShoppingDataInitialLoad,
  ShoppingFilterItem
} from '@models/shopping.model';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { ActivityStateService } from '@services/activity/state/activity-state.service';
import { ModalService } from '@services/modal/modal.service';
import { SessionService } from '@services/session/session.service';
import { ShoppingService } from '@services/shopping/shopping.service';
import { TranslationService } from '@services/translation/translation.service';
import { PriceSummaryComponent } from '@shared-components/price-summary/price-summary.component';
import { Subject, asapScheduler } from 'rxjs';
import { DataLoaderCaptureOverrideDirective, DataLoaderService } from 'tangoe-design-system';

import { AdminOverrideBadgeComponent } from '../admin-override/admin-override-badge/admin-override-badge.component';
import { AdminOverrideBannerComponent } from '../admin-override/admin-override-banner/admin-override-banner.component';
import { CarrierComponent } from '../customize-device/carrier/carrier.component';
import { DeviceDescriptionComponent } from '../customize-device/device-description/device-description.component';
import { ImagePlaceholderComponent } from '../customize-device/image-placeholder/image-placeholder.component';

@Component({
  selector: 'ts-device-details-purchase',
  templateUrl: './device-details-purchase.component.html',
  hostDirectives: [DataLoaderCaptureOverrideDirective],
  imports: [
    CommonModule,
    TranslationPipe,
    AsButtonDirective,
    AdminOverrideBadgeComponent,
    AdminOverrideBannerComponent,
    CarrierComponent,
    DeviceDescriptionComponent,
    ImagePlaceholderComponent,
    PriceSummaryComponent
  ]
})
export class DeviceDetailsPurchaseComponent implements OnInit, OnDestroy {
  current_activity_id: number;
  selected_device_name: string | null;
  selected_device_id: number;
  selected_devices_by_carrier: DeviceItemData;
  selected_carrier_id: number | string;
  selected_normalized_color: string;
  selected_storage_size: string;
  selected_name_override_id: number = -1;
  selected_storage_device_price: DevicePrice = {
    onetime: { cost_to_currency: '' },
    monthly: { cost_to_currency: '' }
  };
  selected_storage_device_id: number = -1;
  selected_color_device_id: number = -1;
  name_override_restore_id: number = -1;
  storage_auto_select_needed: boolean = false;
  storage_reselect_needed: boolean = false;
  device_selection_device_id: number | undefined = undefined;
  device_data: DeviceData;
  device_record: DeviceItemData[];
  normalized_device_list_by_color: DeviceItemDevice[];
  active_device: DeviceItemDevice;
  storage_list: DeviceItemDevice[] = [];
  name_override_list: DeviceItemDevice[] = [];
  is_display_one_time_cost: boolean = true;
  is_display_recurring_cost: boolean = true;
  existing_image_url: string | undefined;
  show_options: boolean = false;
  refresh_name_overrides_after_admin_toggle: boolean = false;
  accessories_enabled: boolean = true;
  devices_payload: any = {
    filters: [],
    orderTypeId: 0,
    categories: [],
    countryId: 0,
    include_admin_options: undefined,
    isDeviceItemRequest: true
  };
  device_plan: ShoppingDataInitialLoad = {
    categories: [],
    filters: [],
    orderTypeId: 0,
    deviceId: null
  };
  shopping_cart: ShoppingCartData = {
    shopping_cart_items: [],
    orderTypeId: 0,
    lineId: undefined
  };
  shopping_items_price: ShoppingCartData = {
    shopping_cart_items: [],
    orderTypeId: 0,
    lineId: undefined
  };
  in_cart_device: DeviceItemDevice | null;
  revisiting: boolean = false;
  modal_dismissed: boolean = false;
  progress_bar_config: ActivityStep[];
  refresh_summary: Subject<boolean | string> = new Subject<boolean | string>();

  constructor(
    private activated_route: ActivatedRoute,
    private activity_state_svc: ActivityStateService,
    private data_loader_svc: DataLoaderService,
    private modal_svc: ModalService,
    private router: Router,
    private session_svc: SessionService,
    private shopping_svc: ShoppingService,
    private translation_svc: TranslationService
  ) {}

  ngOnInit(): void {
    this.current_activity_id =
      this.device_plan.orderTypeId =
      this.devices_payload.orderTypeId =
      this.shopping_cart.orderTypeId =
      this.shopping_items_price.orderTypeId =
        this.activity_state_svc.orderTypeId()!;

    this.shopping_cart.lineId = this.shopping_items_price.lineId =
      this.activity_state_svc.parameter('lineId');

    this.devices_payload.include_admin_options = this.shopping_cart.device_include_admin_options =
      this.activity_state_svc.parameter('device_include_admin_options');

    this.activated_route.queryParamMap.subscribe({
      next: (paramMap) => {
        const carrierId: string | null = paramMap.get('carrierId');
        if (!this.selected_devices_by_carrier) {
          this.selected_carrier_id = carrierId === 'none' ? 'none' : Number(carrierId);
        }
        this.selected_device_id = Number(paramMap.get('deviceId'));
        this.selected_device_name = paramMap.get('name');
        this.devices_payload.categories = JSON.parse(paramMap.get('categories') as string);
        this.devices_payload.countryId = this.activity_state_svc.parameter('country')?.id;

        const filter: ShoppingFilterItem = {
          id: Number(paramMap.get('filterConfigId')),
          value: this.selected_device_name!
        };
        if (
          this.devices_payload.filters.every(
            (f: ShoppingFilterItem) => f.id !== filter.id && f.value !== filter.value
          )
        )
          this.devices_payload.filters.push(filter);
      },
      error: (err: HttpErrorResponse) => {}
    });
    this.loadDevice();
    this.data_loader_svc.toggle(false);
  }

  ngOnDestroy(): void {
    this.shopping_svc.preselected_item = false;
  }

  get page_to_navigate(): string {
    return this.accessories_enabled
      ? `shop/${this.activity_state_svc.orderTypeId()}/accessories`
      : `shop/${this.activity_state_svc.orderTypeId()}/finalize-order`;
  }

  get content_loading(): boolean {
    return (
      this.data_loader_svc.isLoading('device-plans') ||
      this.data_loader_svc.isLoading('device-plan-features')
    );
  }

  get is_admin(): boolean {
    return this.session_svc.getUserProfile()?.admin ?? false;
  }

  get disable_admin_overide(): boolean {
    return this.device_data?.admin_options === ADMIN_OPTIONS.ALL_ADMIN_OPTIONS;
  }

  get admin_cart_items(): boolean {
    return CartInfo.cart_data?.shopping_cart_items.some(
      (item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_DEVICE && item.additional_info?.admin
    )!;
  }

  get disable_continue_btn(): boolean {
    const cart_params: string[] = [CART_ITEM_TYPE_DEVICE];

    let valid_cart: boolean =
      this.shopping_cart.shopping_cart_items.filter((item: ShoppingCartItem) =>
        cart_params.includes(item.type)
      ).length === cart_params.length;

    if (this.show_options) valid_cart = valid_cart && this.selected_name_override_id !== -1;

    return !valid_cart;
  }

  appendMonthlyLabel = (cost: string): string => (cost.match(/mo(nth)?$/i) ? cost : `${cost}/mo`);

  getItemByType = (type: string): ShoppingCartItem | undefined =>
    this.shopping_cart.shopping_cart_items.find((item: ShoppingCartItem) => item.type === type);

  getItemById = (id: string | number): ShoppingCartItem | undefined =>
    this.shopping_cart.shopping_cart_items.find((item: ShoppingCartItem) => item.id == id);

  getActiveStorage = (device: DeviceItemDevice): boolean =>
    device.size === this.selected_storage_size &&
    device.price.onetime.cost_to_currency ===
      this.selected_storage_device_price.onetime.cost_to_currency &&
    (device.price.monthly
      ? device.price.monthly?.cost_to_currency ===
        this.selected_storage_device_price.monthly.cost_to_currency
      : true);

  confirmAdminToggleClearCart(admin_override: boolean): void {
    this.shopping_svc.getShoppingItems().subscribe({
      next: (res: ShoppingCartData) => {
        let cart_items_to_delete: ShoppingCartItem[] = res.shopping_cart_items.filter(
          (cart_item: ShoppingCartItem) => cart_item.type !== CART_ITEM_TYPE_ACCESSORY
        );
        this.confirmChangesModal(
          () => this.getAdminOverride(admin_override, true),
          cart_items_to_delete
        );
      }
    });
  }

  getAdminOverride(admin_override: boolean, clear_cart: boolean = false): void {
    if (!this.disable_admin_overide) {
      if (this.selected_name_override_id !== -1)
        this.name_override_restore_id = this.selected_name_override_id;

      this.devices_payload.include_admin_options = this.shopping_cart.device_include_admin_options =
        admin_override;
      const is_device_admin: boolean =
        !!this.getItemByType(CART_ITEM_TYPE_DEVICE)?.additional_info?.admin;

      const must_clear_selection: boolean = is_device_admin || clear_cart;

      if (must_clear_selection) {
        this.selected_storage_device_id = -1;
        this.selected_name_override_id = -1;

        if (clear_cart) this.selected_storage_size = '';

        this.storage_auto_select_needed = clear_cart;
        this.storage_reselect_needed = !clear_cart;
      } else {
        this.storage_reselect_needed = true;
        this.storage_auto_select_needed = false;
      }

      this.name_override_list = [];
      this.show_options = false;
      this.refresh_name_overrides_after_admin_toggle = true;

      this.loadDevice(() => {
        this.getShoppingCartData(this.shopping_cart, true);

        const restoredNameOverride = this.name_override_list.find(
          (device: DeviceItemDevice) => device.id === this.name_override_restore_id
        );

        if (restoredNameOverride) this.selectNameOverride(restoredNameOverride, true);

        this.name_override_restore_id = -1;
      });
    }
  }

  selectCarrierClick(carrier: DeviceItemCarrier): void {
    const device_item = this.device_record.find(
      (item: DeviceItemData) => item.carrier.id === carrier.id
    );

    if (!device_item) return;

    if (this.revisiting && carrier.id != this.selected_carrier_id && !this.modal_dismissed) {
      this.confirmChangesModal(() => this.selectCarrier(device_item));
    } else this.selectCarrier(device_item);
  }

  selectColorClick(device_details: DeviceItemDevice): void {
    if (
      this.revisiting &&
      device_details.id != this.selected_color_device_id &&
      !this.modal_dismissed
    ) {
      this.confirmChangesModal(() => this.selectColor(device_details));
    } else this.selectColor(device_details);
  }

  selectStorageClick(device_details: DeviceItemDevice): void {
    if (
      this.revisiting &&
      (device_details.size != this.selected_storage_size ||
        device_details.id != this.selected_device_id) &&
      this.selected_storage_size !== '' &&
      !this.modal_dismissed
    ) {
      this.confirmChangesModal(() => this.selectStorage(device_details));
    } else this.selectStorage(device_details);
  }

  selectNameOverrideClick(device_details: DeviceItemDevice): void {
    if (
      this.revisiting &&
      device_details.id !== this.selected_name_override_id &&
      this.selected_name_override_id !== -1 &&
      !this.modal_dismissed
    ) {
      this.confirmChangesModal(() => this.selectNameOverride(device_details));
    } else this.selectNameOverride(device_details);
  }

  getShoppingCartData(shopping_cart: ShoppingCartData, device_refreshed: boolean = false): void {
    if (shopping_cart) {
      if (!device_refreshed)
        this.revisiting =
          shopping_cart.shopping_cart_items.filter(
            (item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_DEVICE
          ).length > 0;

      shopping_cart.shopping_cart_items.forEach((item: ShoppingCartItem) => {
        if (!this.getItemById(item.id))
          this.generateShoppingItems(item, item.type, false, item.additional_info?.category_id);
      });

      const cart_objects: InCartObjects = this.retrieveCartObjects();

      if (cart_objects.device_id || cart_objects.plan_id) {
        if (this.in_cart_device) {
          this.selected_storage_device_id = this.storage_list.find((device: DeviceItemDevice) => {
            return (
              (device.size === this.in_cart_device?.size &&
                device.price.onetime.cost_to_currency ===
                  this.in_cart_device?.price.onetime.cost_to_currency &&
                (this.in_cart_device?.price.monthly
                  ? this.in_cart_device.price.monthly.cost_to_currency ===
                    device.price.monthly?.cost_to_currency
                  : true)) ||
              device.id === this.in_cart_device?.id
            );
          })!.id;
          this.selected_device_id = this.selected_name_override_id = this.in_cart_device.id;
        } else if (cart_objects.device_id)
          this.selected_storage_device_id = this.selected_device_id = cart_objects.device_id;

        if (
          this.selected_name_override_id !== -1 ||
          this.selected_storage_device_id !== -1 ||
          this.in_cart_device
        )
          this.selectStorage(
            ([...this.name_override_list, ...this.storage_list].find((device: DeviceItemDevice) =>
              [this.selected_name_override_id, this.selected_storage_device_id].includes(device.id)
            ) as DeviceItemDevice) ?? this.in_cart_device
          );
      }

      const has_accessories: boolean = shopping_cart.shopping_cart_items.some(
        (item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_ACCESSORY
      );

      if (!!this.getItemByType(CART_ITEM_TYPE_DEVICE) || has_accessories) {
        this.is_display_one_time_cost = true;
        this.is_display_recurring_cost = false;
      } else {
        this.is_display_one_time_cost = false;
        this.is_display_recurring_cost = false;
      }

      this.setCartValues(shopping_cart);
    }
  }

  private loadDevice(callback?: Function): void {
    this.shopping_svc.getDevices(this.devices_payload, 'global').subscribe({
      next: (res: DeviceData) => {
        if (res.items.length) {
          this.device_data = res;
          this.accessories_enabled = res.progress_bar_config.is_accessory_ordering_enabled;
          this.progress_bar_config = res.progress_bar_config.steps;
          this.device_record = res.items as DeviceItemData[];
          this.in_cart_device = res.in_cart_device;
          this.assignData();

          if (this.device_record && callback) asapScheduler.schedule(() => callback());
        }
      },
      error: (err: HttpErrorResponse) => {}
    });
  }

  private assignData(device_record?: DeviceItemData): void {
    this.selected_devices_by_carrier = !device_record
      ? (this.device_record.find(
          (item: DeviceItemData) => item.carrier.id == this.selected_carrier_id
        ) as DeviceItemData) || this.device_record[0]
      : device_record;

    this.selected_carrier_id = this.selected_devices_by_carrier.carrier.id;
    this.normalized_device_list_by_color = this.getUniqColorWithOrder(
      this.selected_devices_by_carrier.devices
    );

    this.active_device =
      this.selected_devices_by_carrier.devices.find(
        (device: DeviceItemDevice) => device.id == this.selected_device_id
      ) || this.normalized_device_list_by_color[0];
    this.selected_normalized_color = this.active_device.color;

    this.selected_device_id = this.active_device.id;
    this.loadStorageList(this.active_device || this.normalized_device_list_by_color[0]);
    this.updateQueryParams();
  }

  private getUniqColorWithOrder(device_list: DeviceItemDevice[]): DeviceItemDevice[] {
    let sorted_color_list: DeviceItemDevice[] = this.shopping_svc.sortColorByName(
      this.shopping_svc.uniqColorByName(device_list)
    );

    if (this.in_cart_device)
      this.selected_color_device_id = this.selected_device_id =
        sorted_color_list.find(
          (device: DeviceItemDevice) => device.color === this.in_cart_device?.color
        )?.id ?? sorted_color_list[0]?.id;
    else
      this.selected_color_device_id = this.selected_device_id =
        (sorted_color_list.find(
          (device: DeviceItemDevice) =>
            device.original_color ===
            this.getItemByType(CART_ITEM_TYPE_DEVICE)?.additional_info?.color
        )?.id as number) ?? sorted_color_list[0].id;

    return sorted_color_list;
  }

  private loadStorageList(device_details: DeviceItemDevice): void {
    const storage_sizes: string[] = ['MB', 'GB', 'TB'];
    let final_storage_list: DeviceItemDevice[] = [];

    let initial_storage_list: DeviceItemDevice[] = this.selected_devices_by_carrier.devices
      .filter(
        (device: DeviceItemDevice) =>
          device.color.toLowerCase() === device_details.color.toLowerCase()
      )
      .sort(
        (a: DeviceItemDevice, b: DeviceItemDevice) =>
          Number(a.size.slice(0, -2)) - Number(b.size.slice(0, -2))
      )
      .sort(
        (a: DeviceItemDevice, b: DeviceItemDevice) =>
          storage_sizes.indexOf(a.size.slice(a.size.length - 2)) -
          storage_sizes.indexOf(b.size.slice(b.size.length - 2))
      );

    initial_storage_list.forEach((i: DeviceItemDevice) => {
      let count: number = i.price.monthly
        ? final_storage_list.filter(
            (f: DeviceItemDevice) =>
              f.size === i.size &&
              f.price.onetime.cost_to_currency === i.price.onetime.cost_to_currency &&
              f.price.monthly.cost_to_currency === i.price.monthly.cost_to_currency
          ).length
        : final_storage_list.filter(
            (f: DeviceItemDevice) =>
              f.size === i.size &&
              f.price.onetime.cost_to_currency === i.price.onetime.cost_to_currency
          ).length;

      if (count === 0) final_storage_list.push(i);
    });

    this.storage_list = final_storage_list;
    this.loadStorageListPostCheck();
  }

  private loadStorageListPostCheck(): void {
    if (this.refresh_name_overrides_after_admin_toggle) {
      this.refresh_name_overrides_after_admin_toggle = false;
      const selectedStorage: DeviceItemDevice | undefined =
        this.storage_list.find(
          (device: DeviceItemDevice) =>
            device.id === this.selected_storage_device_id ||
            (device.size === this.selected_storage_size &&
              device.price.onetime.cost_to_currency ===
                this.selected_storage_device_price.onetime.cost_to_currency)
        ) ?? this.storage_list[0];

      if (selectedStorage) this.selectStorage(selectedStorage);
      return;
    }

    if (this.storage_list.length === 1 && !this.getItemByType(CART_ITEM_TYPE_DEVICE)) {
      this.shopping_svc.preselected_item = true;
      this.selectStorage(this.in_cart_device || this.storage_list[0]);
    } else if (this.storage_list.length > 1 && !this.getItemByType(CART_ITEM_TYPE_DEVICE)) {
      // Preserve one-time cost visibility if accessories exist
      const existing_accessories = this.shopping_cart.shopping_cart_items.some(
        (item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_ACCESSORY
      );
      this.is_display_one_time_cost = existing_accessories;
      this.is_display_recurring_cost = false;
    }
  }

  private loadNameOverrides(
    device_details: DeviceItemDevice,
    allow_plan_load: boolean = false
  ): void {
    this.name_override_list = this.selected_devices_by_carrier.devices.filter(
      (device: DeviceItemDevice) =>
        device.color === this.selected_normalized_color &&
        device.size === this.selected_storage_size &&
        device.price.onetime.cost_to_currency ===
          this.selected_storage_device_price.onetime.cost_to_currency &&
        (device_details.price.monthly
          ? device.price.monthly.cost_to_currency ===
            this.selected_storage_device_price.monthly.cost_to_currency
          : true)
    );

    this.loadNameOverridePostCheck(device_details, allow_plan_load);
  }

  private loadNameOverridePostCheck(
    device_details: DeviceItemDevice,
    allow_plan_load: boolean = false
  ): void {
    this.show_options =
      this.name_override_list.length > 1 ||
      (this.name_override_list.length > 0 &&
        this.name_override_list.every((device: DeviceItemDevice) => device.name_override));

    if (this.show_options) {
      this.name_override_list.map((device: DeviceItemDevice) => {
        if (!device.name_override) device.name_override = device.name;
      });
    }

    const has_selected_override: boolean = this.selected_name_override_id !== -1;
    const selected_override: DeviceItemDevice | undefined = has_selected_override
      ? this.name_override_list.find(
          (device: DeviceItemDevice) => device.id === this.selected_name_override_id
        )
      : undefined;

    if (this.name_override_list.length === 0) {
      this.device_plan.deviceId = device_details.id;
      if (
        !this.shopping_cart.shopping_cart_items.find(
          (cart_item: ShoppingCartItem) => cart_item.type === CART_ITEM_TYPE_DEVICE
        )
      )
        this.generateShoppingItems(
          device_details,
          CART_ITEM_TYPE_DEVICE,
          true,
          this.devices_payload.categories[0].id
        );
    } else if (this.name_override_list.length > 1 && this.selected_name_override_id === -1) {
      if (
        !this.shopping_cart.shopping_cart_items.find(
          (cart_item: ShoppingCartItem) => cart_item.type === CART_ITEM_TYPE_DEVICE
        )
      )
        this.generateShoppingItems(
          device_details,
          CART_ITEM_TYPE_DEVICE,
          true,
          this.devices_payload.categories[0].id
        );
    } else if (this.name_override_list.length === 1)
      this.selectNameOverride(this.name_override_list[0], true);
    else if (selected_override && allow_plan_load) this.selectNameOverride(selected_override, true);
  }

  private confirmChangesModal(callback: () => void, admin_cart_items?: ShoppingCartItem[]): void {
    this.modal_svc.confirmationDialog(
      {
        title: this.translation_svc.getTranslatedModalLabels()[0],
        message: this.translation_svc.getTranslatedModalLabels()[1],
        confirm_button_text: this.translation_svc.translate('continue'),
        cancel_button_text: this.translation_svc.translate('cancel'),
        onConfirm: () => {
          this.shopping_svc
            .clearSpecifiedCartItems(
              calculateItemsToDelete(admin_cart_items ?? this.shopping_cart.shopping_cart_items),
              false
            )
            .subscribe({
              next: () => {
                this.revisiting = false;
                this.in_cart_device = null;
                callback();
                this.modal_dismissed = true;
                this.modal_svc.dismissAll();
              },
              error: () => {}
            });
        }
      },
      {
        size: 'dialog-centered',
        centered: true,
        backdrop: 'static'
      }
    );
  }

  private selectCarrier(device_item: DeviceItemData): void {
    if (this.selected_carrier_id == device_item.carrier.id) return;

    // Preserve accessories before reset
    const preserved_accessories: ShoppingCartItem[] = this.shopping_cart.shopping_cart_items.filter(
      (item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_ACCESSORY
    );
    const preserved_price_accessories: ShoppingCartItem[] =
      this.shopping_items_price.shopping_cart_items.filter(
        (item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_ACCESSORY
      );
    this.resetDeviceSelection();

    // Reset color/storage selections
    this.selected_color_device_id = -1;
    this.selected_storage_device_id = -1;
    this.selected_storage_size = '';
    this.selected_normalized_color = '';
    this.active_device = null!;

    // Restore preserved accessories after reset
    this.shopping_cart.shopping_cart_items = [...preserved_accessories];
    this.shopping_items_price.shopping_cart_items = [...preserved_price_accessories];

    // Only one-time cost should be shown in this customize-order flow.
    if (preserved_accessories.length) {
      this.is_display_one_time_cost = true;
      this.is_display_recurring_cost = false;
    } else {
      this.is_display_one_time_cost = false;
      this.is_display_recurring_cost = false;
    }

    this.assignData(device_item);
    this.refresh_summary.next(true);
  }

  private selectColor(device_details: DeviceItemDevice): void {
    if (this.selected_color_device_id === device_details.id) return;

    this.active_device = device_details;
    this.resetDeviceSelection();

    this.selected_device_id = this.active_device.id;
    this.selected_normalized_color = this.active_device.color;
    this.selected_color_device_id = device_details.id;
    this.is_display_one_time_cost = true;
    this.updateQueryParams();
    this.loadStorageList(this.active_device);
  }

  private selectStorage(device_details: DeviceItemDevice): void {
    if (
      this.selected_storage_size === device_details.size &&
      device_details.id === this.selected_storage_device_id &&
      this.name_override_list.length > 0
    )
      return;

    this.activity_state_svc.setSteps(this.progress_bar_config);

    if (
      !this.revisiting &&
      !this.shopping_svc.preselected_item &&
      this.selected_storage_device_id !== -1 &&
      device_details.id !== this.selected_storage_device_id
    )
      this.resetDeviceSelection();

    this.selected_storage_device_id = device_details.id;
    this.selected_storage_device_price.onetime = device_details.price.onetime;
    this.selected_storage_device_price.monthly = device_details.price.monthly;

    this.selected_storage_size = device_details.size;
    this.is_display_one_time_cost = true;
    this.is_display_recurring_cost = false;

    this.loadNameOverrides(device_details, true);
  }

  private selectNameOverride(device_details: DeviceItemDevice, force_load: boolean = false): void {
    if (!force_load && this.selected_name_override_id === device_details.id) return;

    this.activity_state_svc.setSteps(this.progress_bar_config);

    this.removeItems(CART_ITEM_TYPE_DEVICE);

    this.device_plan.deviceId = this.selected_name_override_id = device_details.id;
    this.is_display_one_time_cost = true;
    this.is_display_recurring_cost = false;

    if (this.show_options && this.shopping_cart.existing_asset) {
      if (!this.device_selection_device_id && this.shopping_cart.existing_asset)
        this.device_selection_device_id = this.shopping_cart.existing_asset.device.id;

      this.shopping_cart.existing_asset = {
        device: {
          existing_asset_id: this.device_selection_device_id,
          id: device_details.id,
          manufacturer: device_details.manufacturer,
          model: device_details.model,
          color: device_details.color,
          type: device_details.product_category,
          imei: this.shopping_cart.existing_asset?.device.imei,
          device_catalog_id: device_details.device_catalog_id,
          size: device_details.size,
          serial_number: this.shopping_cart.existing_asset?.device.serial_number,
          tech_specs: {
            sim_compatibility:
              this.shopping_cart.existing_asset?.device.tech_specs.sim_compatibility
          },
          image_url: device_details.image_url,
          normalized_color: device_details.original_color,
          is_override: true
        },
        asset_from_inventory: this.shopping_cart.existing_asset.asset_from_inventory,
        asset_selection_case: this.shopping_cart.existing_asset.asset_selection_case,
        device_identifier_search_value:
          this.shopping_cart.existing_asset?.device_identifier_search_value
      } as UserAsset;
    }

    this.generateShoppingItems(
      device_details,
      CART_ITEM_TYPE_DEVICE,
      true,
      this.devices_payload.categories[0].id
    );
  }

  private generateShoppingItems(
    new_cart_item: any,
    new_cart_type: string,
    refresh: boolean = false,
    category_id?: number
  ): void {
    if (category_id) new_cart_item.category_id = category_id;

    this.shopping_cart.shopping_cart_items.push(
      createShoppingObject(
        new_cart_item,
        new_cart_type,
        this.selected_devices_by_carrier.carrier.image_url
      )
    );
    this.shopping_items_price.shopping_cart_items.push(
      createShoppingPriceObject(new_cart_item, new_cart_type)
    );

    this.shopping_cart.carrier = this.selected_devices_by_carrier.carrier;
    this.createPayloadString();
    this.refresh_summary.next(refresh);
  }

  private createPayloadString(): void {
    this.shopping_cart.shopping_cart_items.map(
      (item) => (item.edit_link_payload = JSON.stringify(this.generateQueryParams()))
    );
  }

  private updateQueryParams(): void {
    this.router
      .navigate([], {
        relativeTo: this.activated_route,
        queryParams: this.generateQueryParams(),
        replaceUrl: true,
        queryParamsHandling: 'merge'
      })
      .then(() => {
        this.activity_state_svc.setSteps(this.progress_bar_config);
      });
  }

  private removeItems(type: string): void {
    this.shopping_cart.shopping_cart_items = this.shopping_cart.shopping_cart_items.filter(
      (item: ShoppingCartItem) => item.type != type
    );
    this.shopping_items_price.shopping_cart_items =
      this.shopping_items_price.shopping_cart_items.filter(
        (item: ShoppingCartItem) => item.type != type
      );
  }

  private retrieveCartObjects(device_details?: DeviceItemDevice): InCartObjects {
    const device_item: ShoppingCartItem | undefined = this.shopping_cart?.shopping_cart_items.find(
      (item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_DEVICE
    );

    const resolved_device_id: number | string | undefined =
      device_details?.id && device_details.id !== device_item?.id
        ? device_details.id
        : device_item?.id;
    const device_id: number = resolved_device_id !== undefined ? Number(resolved_device_id) : -1;

    return {
      device_id: device_id,
      plan_id: 0,
      features: []
    };
  }

  private generateQueryParams() {
    return {
      carrierId: this.selected_carrier_id,
      name: this.selected_device_name,
      filterConfigId: this.devices_payload.filters[0].id,
      filters: undefined,
      categories: JSON.stringify(this.devices_payload.categories),
      countryId: this.devices_payload.countryId,
      deviceId:
        this.in_cart_device?.id ??
        (this.getItemByType(CART_ITEM_TYPE_DEVICE)?.id as number) ??
        this.selected_device_id,
      number_of_plans: 0,
      number_of_features: 0
    };
  }

  private setCartValues(shopping_cart: ShoppingCartData): void {
    this.shopping_cart.order_for_person = shopping_cart.order_for_person;
    this.shopping_cart.fields = shopping_cart.fields;
    this.shopping_cart.device_identifier = shopping_cart.device_identifier;
    this.shopping_cart.order_sim = shopping_cart.order_sim;
    this.shopping_cart.new_sim = shopping_cart.new_sim;
    this.shopping_cart.esim_activation = shopping_cart.esim_activation;
    this.shopping_cart.new_eid = shopping_cart.new_eid;
    this.shopping_cart.device_include_admin_options = shopping_cart.device_include_admin_options;
    this.shopping_cart.accessories_include_admin_options =
      shopping_cart.accessories_include_admin_options;
    this.shopping_cart.order_type_settings = shopping_cart.order_type_settings;
    this.shopping_cart.quantity = shopping_cart.quantity;
  }

  private resetDeviceSelection(): void {
    this.shopping_svc.preselected_item = false;
    this.selected_storage_size = '';
    this.name_override_list = [];
    this.selected_name_override_id = -1;

    this.removeItems(CART_ITEM_TYPE_DEVICE);

    const existing_accessories: boolean = this.shopping_cart.shopping_cart_items.some(
      (cart_item: ShoppingCartItem) => cart_item.type === CART_ITEM_TYPE_ACCESSORY
    );

    this.is_display_one_time_cost = existing_accessories;
    this.is_display_recurring_cost = false;

    this.device_plan.deviceId = null;
    this.refresh_summary.next(true);
  }
}
